/**
 * Zustand App Store - Vanilla API
 *
 * Centralized app state management using Zustand with Vanilla API
 * for access both inside and outside React components.
 *
 * Features:
 * - Vanilla API for non-React access
 * - localStorage persistence
 * - Type-safe actions and state
 * - App switching (main, admin, org-admin)
 * - Organization selection
 *
 * This store consolidates functionality from:
 * - AppContext (state management)
 * - app-context-storage (localStorage utilities)
 *
 * Navigation is handled separately via useAppNavigation hook
 * which combines this store with TanStack Router.
 */

import { useEffect, useState } from "react";
import {
	createJSONStorage,
	persist,
	subscribeWithSelector,
} from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import type { Organization } from "@/services/organization";
import { OrganizationStatusCode } from "@/services/organization";
import { registerMixpanelOrgSource } from "@/stores/mixpanel-org-source";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * App type - defines which app section the user is currently in
 */
export type AppType = "main" | "admin" | "org-admin";

/**
 * App Store State Interface
 */
export interface AppState {
	/** Current app section (main, admin, or org-admin) */
	currentApp: AppType;
	/** Currently selected organization (for admin/org-admin apps) */
	currentOrganization: Organization | null;
	/** List of organizations available to the user */
	availableOrganizations: Organization[];
}

/**
 * App Store Actions Interface
 */
export interface AppActions {
	// State setters
	setApp: (app: AppType) => void;
	setOrganization: (orgId: string) => void;
	setAvailableOrganizations: (organizations: Organization[]) => void;

	// Computed/derived state getters
	getCanAccessAdmin: () => boolean;
	getCanAccessMultipleOrgs: () => boolean;
	getIsMainApp: () => boolean;
	getIsAdminApp: () => boolean;
	getIsOrgAdminApp: () => boolean;

	// Lifecycle
	reset: () => void;
}

/**
 * Combined App Store Type
 */
export type AppStore = AppState & AppActions;

// ============================================================================
// TEMPORARY DATA (Replace with API calls later)
// ============================================================================

/**
 * Temporary organizations - hardcoded until API is ready
 *
 * TODO: Replace with API call to fetch user's organizations
 * Endpoint: GET /api/users/{userId}/organizations
 */
export const TEMP_ORGANIZATIONS: Organization[] = [
	{
		id: "all",
		name: "All Organizations",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-acme",
		name: "Acme Corporation",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-techcorp",
		name: "TechCorp Solutions",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-innovate",
		name: "Innovate Industries",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
];

// ============================================================================
// INITIAL STATE
// ============================================================================

/**
 * Initial App State
 */
const initialState: AppState = {
	currentApp: "main",
	currentOrganization: null,
	availableOrganizations: [],
};

const createOrganizationReference = (id: string): Organization =>
	({ id, name: null }) as Organization;

// ============================================================================
// PERSISTED STATE TYPE
// ============================================================================

/**
 * Persisted state type - only these fields are saved to localStorage
 */
type PersistedAppState = Pick<AppState, "currentApp"> & {
	currentOrganizationId: string | null;
};

// ============================================================================
// STORE CREATION
// ============================================================================

/**
 * Create Zustand App Store with Vanilla API and Persist Middleware
 */
export const appStore = createStore<AppStore>()(
	subscribeWithSelector(
		persist(
			(set, get) => ({
				// Initial state
				...initialState,

				// ============================================================================
				// STATE SETTERS
				// ============================================================================

				/**
				 * Set the current app
				 *
				 * NOTE: This only updates state. Navigation should be handled
				 * by components using the useAppNavigation hook.
				 */
				setApp: (app: AppType) => {
					set({ currentApp: app });

					console.log(`📱 AppStore: Switched to app "${app}"`);
				},

				/**
				 * Set the current organization by ID
				 *
				 * NOTE: This only updates state. Navigation should be handled
				 * by components using the useAppNavigation hook.
				 */
				setOrganization: (orgId: string) => {
					const { availableOrganizations } = get();

					const org = availableOrganizations.find((o) => o.id === orgId);
					if (!org) {
						if (orgId !== "all" && orgId !== "unknown") {
							set({ currentOrganization: createOrganizationReference(orgId) });
							return;
						}
						console.warn(
							`⚠️ AppStore: Organization with id "${orgId}" not found`,
						);
						return;
					}

					set({ currentOrganization: org });
				},

				/**
				 * Set available organizations
				 * Called when organizations are fetched from API
				 */
				setAvailableOrganizations: (organizations: Organization[]) => {
					set({ availableOrganizations: organizations });

					const { currentOrganization } = get();
					if (currentOrganization) {
						const fullOrganization = organizations.find(
							(org) => org.id === currentOrganization.id,
						);
						if (fullOrganization) {
							set({ currentOrganization: fullOrganization });
						}
					}

					console.log(
						`📋 AppStore: Updated available organizations (${organizations.length})`,
					);
				},

				// ============================================================================
				// COMPUTED/DERIVED STATE GETTERS
				// ============================================================================

				/**
				 * Check if user can access admin app
				 *
				 * TODO: Integrate with auth permissions
				 * Currently returns true for all users
				 */
				getCanAccessAdmin: () => {
					// TODO: Replace with actual permission check from auth store
					// Example: return authStore.getState().hasPermission('access:admin');
					return true;
				},

				/**
				 * Check if user can access multiple organizations
				 */
				getCanAccessMultipleOrgs: () => {
					return get().availableOrganizations.length > 1;
				},

				/**
				 * Check if currently in main app
				 */
				getIsMainApp: () => {
					return get().currentApp === "main";
				},

				/**
				 * Check if currently in admin app
				 */
				getIsAdminApp: () => {
					return get().currentApp === "admin";
				},

				/**
				 * Check if currently in org-admin app
				 */
				getIsOrgAdminApp: () => {
					return get().currentApp === "org-admin";
				},

				// ============================================================================
				// LIFECYCLE
				// ============================================================================

				/**
				 * Reset store to initial state
				 */
				reset: () => {
					set(initialState);
					appStore.persist.clearStorage();
					console.log("🔄 AppStore: Reset to initial state");
				},
			}),
			{
				name: "app_state", // localStorage key
				storage: createJSONStorage(() => localStorage),
				version: 1, // Enables future migrations
				partialize: (state): PersistedAppState => ({
					currentApp: state.currentApp,
					currentOrganizationId: state.currentOrganization?.id ?? null,
				}),
				onRehydrateStorage: () => {
					// console.log("📖 AppStore: Hydration starting...");
					return (state, error) => {
						if (error) {
							console.error("❌ AppStore: Hydration failed:", error);
						} else if (state) {
							// Restore currentOrganization from ID after hydration
							const persistedState = state as unknown as PersistedAppState;
							if (persistedState.currentOrganizationId) {
								state.currentOrganization = createOrganizationReference(
									persistedState.currentOrganizationId,
								);
							}
							// console.log("✅ AppStore: Hydration finished:", {
							// 	currentApp: state.currentApp,
							// 	currentOrganization: state.currentOrganization?.name ?? null,
							// });
						}
					};
				},
			},
		),
	),
);

// ============================================================================
// SYNCHRONOUS ACCESSORS (Non-React)
// ============================================================================

/**
 * Synchronous app state getter for non-React contexts
 */
export const getAppState = () => appStore.getState();

/**
 * Get current app (synchronous)
 */
export const getCurrentApp = () => appStore.getState().currentApp;

/**
 * Get current organization (synchronous)
 */
export const getCurrentOrganization = () =>
	appStore.getState().currentOrganization;

/**
 * Check if in main app (synchronous)
 */
export const isMainApp = () => appStore.getState().currentApp === "main";

/**
 * Check if in admin app (synchronous)
 */
export const isAdminApp = () => appStore.getState().currentApp === "admin";

/**
 * Check if in org-admin app (synchronous)
 */
export const isOrgAdminApp = () =>
	appStore.getState().currentApp === "org-admin";

// ============================================================================
// SUBSCRIPTION UTILITIES
// ============================================================================

/**
 * Subscribe to app state changes
 */
export const subscribeToAppState = (callback: (state: AppState) => void) => {
	return appStore.subscribe(callback);
};

// ============================================================================
// REACT HYDRATION HOOK
// ============================================================================

/**
 * React hook to check if the app store has been hydrated from localStorage
 * Useful for preventing flash of incorrect app state
 */
export const useAppHydration = () => {
	const [hydrated, setHydrated] = useState(appStore.persist.hasHydrated());

	useEffect(() => {
		// Subscribe to hydration events
		const unsubFinishHydration = appStore.persist.onFinishHydration(() =>
			setHydrated(true),
		);

		// Check current hydration status (in case it already finished)
		setHydrated(appStore.persist.hasHydrated());

		return () => {
			unsubFinishHydration();
		};
	}, []);

	return hydrated;
};

/**
 * Check if app store has been hydrated (synchronous, non-React)
 */
export const hasAppHydrated = () => appStore.persist.hasHydrated();

// ============================================================================
// ORGANIZATION UTILITIES
// ============================================================================

/**
 * Find organization by ID
 */
export const findOrganization = (orgId: string): Organization | undefined => {
	return appStore.getState().availableOrganizations.find((o) => o.id === orgId);
};

/**
 * Get default organization (first in list)
 */
export const getDefaultOrganization = (): Organization | undefined => {
	return appStore.getState().availableOrganizations[0];
};

/**
 * Check if user has access to multiple organizations
 */
export const hasMultipleOrganizations = (): boolean => {
	return appStore.getState().availableOrganizations.length > 1;
};

registerMixpanelOrgSource(() => {
	const { availableOrganizations, currentApp, currentOrganization } =
		appStore.getState();
	return { availableOrganizations, currentApp, currentOrganization };
});

// Export default store for convenience
export default appStore;
